import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useCompare } from '../../context/CompareContext';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../config/api';
import './ComparePage.css';

const ComparePage = () => {
  const navigate = useNavigate();
  const { compareItems, removeFromCompare, clearCompare } = useCompare();
  const { formatPriceRange } = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch full product details for compare items
  useEffect(() => {
    const fetchProducts = async () => {
      if (compareItems.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const fetched = await Promise.all(
          compareItems.map(async (item) => {
            try {
              const res = await fetch(`${API_BASE_URL}/products/${item.id}`);
              const data = await res.json();
              if (data && (data.product || data.id)) {
                return data.product || data;
              }
              return item; // fallback to stored data
            } catch {
              return item;
            }
          })
        );
        setProducts(fetched);
      } catch {
        setProducts(compareItems);
      }
      setLoading(false);
    };
    fetchProducts();
  }, [compareItems]);

  const getVal = (product, key) => {
    const seller = product?.seller || {};
    switch (key) {
      case 'name': return product.name || product.description || 'N/A';
      case 'image': return product.image || '';
      case 'price': return formatPriceRange(product.priceMin, product.priceMax);
      case 'pricePer': return product.pricePer || 'N/A';
      case 'moq': return product.moq || 'N/A';
      case 'category': return product.category?.name || product.categoryName || 'N/A';
      case 'subcategory': return product.subcategory?.name || product.subcategoryName || 'N/A';
      case 'description': return product.shortDescription || product.description || 'N/A';
      case 'overview': {
        const text = product.overview || product.longDescription || '';
        const stripped = text.replace(/<[^>]+>/g, '');
        return stripped.substring(0, 300) || 'N/A';
      }
      case 'keyFeatures': return product.keyFeatures || [];
      case 'company': return seller.name || product.companyName || 'N/A';
      case 'companyLogo': return seller.logo || product.companyLogo || '';
      case 'country': return seller.country || product.country || 'N/A';
      case 'countryFlag': return seller.countryFlag || product.countryFlag || '';
      case 'businessType': return seller.businessType || product.businessType || 'N/A';
      default: return 'N/A';
    }
  };

  const isDifferent = (key) => {
    if (products.length < 2) return false;
    const v1 = JSON.stringify(getVal(products[0], key));
    const v2 = JSON.stringify(getVal(products[1], key));
    return v1 !== v2;
  };

  const rows = [
    {
      label: 'Image',
      key: 'image',
      render: (product) => {
        const img = getVal(product, 'image');
        return img ? (
          <img src={img} alt={getVal(product, 'name')} className="compare-cell-image" />
        ) : <span className="compare-no-data">No image</span>;
      }
    },
    {
      label: 'Product Name',
      key: 'name',
      render: (product) => <strong className="compare-product-name">{getVal(product, 'name')}</strong>
    },
    {
      label: 'Price',
      key: 'price',
      render: (product) => (
        <div className="compare-price">
          <span className="compare-price-val">{getVal(product, 'price')}</span>
          <span className="compare-price-per">/ {getVal(product, 'pricePer')}</span>
        </div>
      )
    },
    {
      label: 'Min. Order Qty',
      key: 'moq',
      render: (product) => getVal(product, 'moq')
    },
    {
      label: 'Category',
      key: 'category',
      render: (product) => {
        const cat = getVal(product, 'category');
        const sub = getVal(product, 'subcategory');
        return sub !== 'N/A' ? `${cat} / ${sub}` : cat;
      }
    },
    {
      label: 'Description',
      key: 'description',
      render: (product) => <p className="compare-desc-text">{getVal(product, 'description')}</p>
    },
    {
      label: 'Key Features',
      key: 'keyFeatures',
      render: (product) => {
        const features = getVal(product, 'keyFeatures');
        if (!features || features.length === 0) return <span className="compare-no-data">N/A</span>;
        return (
          <ul className="compare-features-list">
            {features.slice(0, 6).map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        );
      }
    },
    {
      label: 'Specifications',
      key: 'overview',
      render: (product) => {
        const text = getVal(product, 'overview');
        return text !== 'N/A' ? <p className="compare-desc-text">{text}</p> : <span className="compare-no-data">N/A</span>;
      }
    },
    {
      label: 'Supplier',
      key: 'company',
      render: (product) => {
        const logo = getVal(product, 'companyLogo');
        const name = getVal(product, 'company');
        return (
          <div className="compare-supplier">
            {logo && <img src={logo} alt="" className="compare-supplier-logo" />}
            <span>{name}</span>
          </div>
        );
      }
    },
    {
      label: 'Country',
      key: 'country',
      render: (product) => {
        const flag = getVal(product, 'countryFlag');
        const country = getVal(product, 'country');
        return (
          <div className="compare-country">
            {flag && <img src={flag} alt="" className="compare-flag" />}
            <span>{country}</span>
          </div>
        );
      }
    },
    {
      label: 'Business Type',
      key: 'businessType',
      render: (product) => getVal(product, 'businessType')
    },
  ];

  return (
    <div className="compare-page">
      <Header />

      <main className="compare-main">
        <div className="compare-breadcrumb">
          <span className="breadcrumb-link" onClick={() => navigate('/')}>Home</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span className="breadcrumb-link" onClick={() => navigate('/products')}>Products</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span className="breadcrumb-current">Compare</span>
        </div>

        <div className="compare-header">
          <h1 className="compare-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 3h5v5"/><path d="M8 3H3v5"/><path d="M21 3l-7 7"/><path d="M3 3l7 7"/><path d="M16 21h5v-5"/><path d="M8 21H3v-5"/><path d="M21 21l-7-7"/><path d="M3 21l7-7"/></svg>
            Compare Products ({compareItems.length}/2)
          </h1>
          {compareItems.length > 0 && (
            <button className="compare-clear-btn" onClick={clearCompare}>Clear All</button>
          )}
        </div>

        {loading ? (
          <div className="compare-loading">Loading product details...</div>
        ) : products.length < 2 ? (
          <div className="compare-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 3h5v5"/><path d="M8 3H3v5"/><path d="M21 3l-7 7"/><path d="M3 3l7 7"/><path d="M16 21h5v-5"/><path d="M8 21H3v-5"/><path d="M21 21l-7-7"/><path d="M3 21l7-7"/></svg>
            <h2>Select 2 products to compare</h2>
            <p>Browse products and click the <strong>Compare</strong> button on product cards to add them here.</p>
            {compareItems.length === 1 && (
              <p className="compare-hint">You have 1 product selected — add 1 more to start comparing.</p>
            )}
            <button className="compare-go-btn" onClick={() => navigate('/products')}>Browse Products</button>
          </div>
        ) : (
          <>
            {/* Mobile card view */}
            <div className="compare-mobile-cards">
              {products.map((product, idx) => (
                <div key={product.id || idx} className="compare-mobile-card">
                  <button className="compare-mobile-remove" onClick={() => removeFromCompare(product.id)}>✕</button>
                  {getVal(product, 'image') && (
                    <img src={getVal(product, 'image')} alt="" className="compare-mobile-img" onClick={() => navigate(`/product/${product.id}`)} />
                  )}
                  <h3 className="compare-mobile-name" onClick={() => navigate(`/product/${product.id}`)}>{getVal(product, 'name')}</h3>
                  <div className="compare-mobile-price">{getVal(product, 'price')} <span>/ {getVal(product, 'pricePer')}</span></div>
                  <div className="compare-mobile-fields">
                    {rows.slice(3).map(row => (
                      <div key={row.label} className="compare-mobile-field">
                        <span className="compare-mobile-label">{row.label}</span>
                        <span className="compare-mobile-value">{row.render(product)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="compare-table-wrapper">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th className="compare-label-col">Feature</th>
                    {products.map((product, idx) => (
                      <th key={product.id || idx} className="compare-item-col">
                        <div className="compare-item-header">
                          <span className="compare-item-name" onClick={() => navigate(`/product/${product.id}`)} style={{ cursor: 'pointer' }}>
                            {getVal(product, 'name')}
                          </span>
                          <button
                            className="compare-remove-btn"
                            onClick={() => removeFromCompare(product.id)}
                            title="Remove from compare"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.label} className={isDifferent(row.key) ? 'compare-row-diff' : ''}>
                      <td className="compare-label-cell">{row.label}</td>
                      {products.map((product, idx) => (
                        <td key={`${product.id || idx}-${row.label}`} className="compare-value-cell">
                          {row.render(product)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ComparePage;
